import React, { useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';
import type { Toast } from '@/stores/uiStore';

// ─── Single toast ─────────────────────────────────────────────────────────────

const VARIANT_STYLES: Record<Toast['variant'], string> = {
  success: 'border-green-500/40 bg-green-900/30 text-green-200',
  error:   'border-red-500/40   bg-red-900/30   text-red-200',
  warning: 'border-yellow-500/40 bg-yellow-900/30 text-yellow-200',
  info:    'border-surface-600   bg-surface-800   text-surface-200',
};

const ICON_PATHS: Record<Toast['variant'], string> = {
  success: 'M5 13l4 4L19 7',
  error:   'M6 18L18 6M6 6l12 12',
  warning: 'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
  info:    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const removeToast = useUIStore((s) => s.removeToast);
  const duration = toast.duration ?? 4000;

  useEffect(() => {
    const timer = setTimeout(() => removeToast(toast.id), duration);
    return () => clearTimeout(timer);
  }, [toast.id, duration, removeToast]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-panel min-w-64 max-w-sm ${VARIANT_STYLES[toast.variant]}`}
      role="alert"
    >
      <svg
        className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={ICON_PATHS[toast.variant]} />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug">{toast.title}</p>
        {toast.description && (
          <p className="text-xs opacity-75 mt-0.5 leading-relaxed">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => removeToast(toast.id)}
        className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ─── Container ────────────────────────────────────────────────────────────────

const ToastContainer: React.FC = () => {
  const toasts = useUIStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none px-4 w-full max-w-md"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
